/**
 * MODULE: platform/browser/frontend/components/BrowserChatPanel.tsx
 * PURPOSE: Browser side assistant — matches shell AI assistant side-rail layout and colors.
 * DEPENDENCIES: browserMissions, BrowserActionLog, BrowserMissionPicker, BrowserAssistantComposer
 * PUBLIC_API: BrowserChatPanel
 * SIDE_EFFECTS: SSE act stream via onMessage; mission start via onStartMission
 * AGENT_SAFETY: SAFE
 * RECENT: 2026-06-18 — Side-rail visual parity with shell AI assistant (canvas, composer card, empty state).
 */

import { EllipsisHorizontalIcon } from '@heroicons/react/24/outline';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { BrowserMission } from '../data/browserMissions';
import { BROWSER_MISSIONS } from '../data/browserMissions';
import type { BrowserCommandInput } from '../hooks/useBrowserCommandInput';
import type { BrowserSession, ChatMessage } from '../types/browser.types';
import { BrowserActionLog } from './BrowserActionLog';
import { BrowserAssistantComposer } from './BrowserAssistantComposer';
import { BrowserAssistantIdleLanding } from './BrowserAssistantIdleLanding';
import { BrowserFollowUpChips } from './BrowserFollowUpChips';
import { BrowserMissionPicker } from './BrowserMissionPicker';
import { BrowserSessionStatus } from './BrowserSessionStatus';
import { dispatchAssistantOpenWithPrefill } from '@design-system/components/AiAssistant/assistantPrefillBridge';
import { ASSISTANT_RAIL_BG_CLASS } from '@design-system/components/AiAssistant/assistantPageTheme';
import {
  lastAssistantOutcome,
  suggestFollowUpCommands,
} from '../utils/browserAssistantOutcome';

interface BrowserChatPanelProps {
  messages: ChatMessage[];
  session: BrowserSession | null;
  isStreaming: boolean;
  isConnecting: boolean;
  suggestedPrompts: string[];
  onMessage: (text: string, context?: string) => void;
  onStartMission: (mission: BrowserMission) => void;
  contextPreview?: string;
  onDismissContextPreview?: () => void;
  sessionTimeoutMinutes?: number;
  onCancelStream?: () => void;
  canRetryLastCommand?: boolean;
  onRetryLastCommand?: () => void;
  retryLastCommandLabel?: string;
  onClearChat?: () => void;
  sessionExpired?: boolean;
  actionCount?: number;
  commandInput: BrowserCommandInput;
  onCollapsePanel?: () => void;
}

export function BrowserChatPanel({
  messages,
  session,
  isStreaming,
  isConnecting,
  suggestedPrompts,
  onMessage,
  onStartMission,
  contextPreview,
  onDismissContextPreview,
  sessionTimeoutMinutes,
  onCancelStream,
  canRetryLastCommand = false,
  onRetryLastCommand,
  retryLastCommandLabel = 'Retry last command',
  onClearChat,
  sessionExpired = false,
  actionCount,
  commandInput,
  onCollapsePanel,
}: BrowserChatPanelProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const focusedSessionRef = useRef<string | null>(null);
  const [logExpanded, setLogExpanded] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { input, setInput, inputRef, inputPlaceholder, handleSend, isDisabled } = commandInput;

  const COMPACT_LOG_TAIL = 4;

  useEffect(() => {
    if (!isStreaming) setLogExpanded(false);
  }, [isStreaming]);

  useEffect(() => {
    if (!session?.id || isStreaming) return;
    if (focusedSessionRef.current === session.id) return;
    focusedSessionRef.current = session.id;
    inputRef.current?.focus();
  }, [session?.id, isStreaming, inputRef]);

  useEffect(() => {
    if (!session?.id) {
      focusedSessionRef.current = null;
    }
  }, [session?.id]);

  useEffect(() => {
    if (!isStreaming || !onCancelStream) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCancelStream();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isStreaming, onCancelStream]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, isStreaming]);

  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menuOpen]);

  const promptChips = useMemo(() => {
    const base = suggestedPrompts.slice(0, 6);
    if (base.length) return base;
    return BROWSER_MISSIONS[0]?.prompts.slice(0, 3) ?? [];
  }, [suggestedPrompts]);

  const previewPrompt = promptChips[0];

  const showMissionPickerAfterLog =
    !session && !isConnecting && !sessionExpired && messages.length > 0;

  const followUpSuggestions = useMemo(() => {
    if (isStreaming || !session) return [];
    const outcome = lastAssistantOutcome(messages);
    if (!outcome || outcome === 'success') return [];
    return suggestFollowUpCommands(outcome);
  }, [isStreaming, session, messages]);

  const compactLog =
    isStreaming && messages.length > COMPACT_LOG_TAIL + 1 && !logExpanded;
  const visibleMessages = compactLog ? messages.slice(-COMPACT_LOG_TAIL) : messages;
  const hiddenLogCount = compactLog ? messages.length - COMPACT_LOG_TAIL : 0;

  const openInAssistant = () => {
    const tail = messages
      .slice(-8)
      .map((m) => `- **${m.role}:** ${m.content}`)
      .join('\n');
    dispatchAssistantOpenWithPrefill(
      ['## Browser automation (in progress)', tail || 'Remote browser session is active.'].join('\n\n'),
      { widgetTitle: 'Browser automation' },
    );
    setMenuOpen(false);
  };

  const isIdleNoSession =
    !session && !isConnecting && !sessionExpired && messages.length === 0 && !isStreaming;

  return (
    <div className={`flex min-h-0 flex-1 flex-col overflow-hidden ${ASSISTANT_RAIL_BG_CLASS}`}>
      <header className="flex shrink-0 items-center justify-between gap-2 border-b border-gray-200/80 px-3 py-2 dark:border-gray-700/60">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Browser Assistant</h3>
          {!session ? (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Remote Chromium — CRM, LinkedIn, Salesforce, and the web
            </p>
          ) : null}
        </div>
        <div className="relative flex shrink-0 items-center gap-1" ref={menuRef}>
          <BrowserSessionStatus
            session={session}
            isConnecting={isConnecting}
            sessionExpired={sessionExpired}
            actionCount={actionCount}
          />
          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            className="rounded-lg p-1.5 text-gray-500 transition hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
            aria-label="Browser assistant menu"
            aria-expanded={menuOpen}
            data-testid="browser-assistant-menu"
          >
            <EllipsisHorizontalIcon className="h-5 w-5" />
          </button>
          {menuOpen ? (
            <div
              role="menu"
              className="absolute right-0 top-full z-20 mt-1 min-w-[11rem] overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-600 dark:bg-gray-800"
            >
              {onCollapsePanel && session?.liveViewUrl ? (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    onCollapsePanel();
                    setMenuOpen(false);
                  }}
                  className="block w-full px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700/60"
                >
                  Maximize view
                </button>
              ) : null}
              {(session || messages.length > 0) ? (
                <button
                  type="button"
                  role="menuitem"
                  onClick={openInAssistant}
                  className="block w-full px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700/60"
                  data-testid="browser-open-in-assistant"
                >
                  Open in Assistant
                </button>
              ) : null}
              {onClearChat ? (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    onClearChat();
                    setMenuOpen(false);
                  }}
                  className="block w-full px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700/60"
                  data-testid="browser-clear-chat"
                >
                  Clear chat
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      </header>

      {isIdleNoSession ? (
        <BrowserAssistantIdleLanding compact>
          <div className="hidden md:block">
            <BrowserMissionPicker
              missions={BROWSER_MISSIONS}
              disabled={isConnecting}
              onSelect={onStartMission}
            />
          </div>
        </BrowserAssistantIdleLanding>
      ) : (
      <div
        ref={scrollRef}
        className="app-shell-disappearing-scroll min-h-0 flex-1 overflow-y-auto px-3 py-3"
      >

        {sessionExpired && messages.length > 0 ? (
          <div
            className="mb-3 rounded-lg border border-amber-200 bg-amber-50/90 px-3 py-2 text-xs text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100"
            data-testid="browser-chat-reconnect-hint"
            role="status"
          >
            Session ended — your action log is preserved. Reconnect in the live view to continue or
            send a follow-up after reconnecting.
          </div>
        ) : null}

        {!session && !isConnecting && messages.length === 0 && sessionExpired ? (
          <div className="hidden space-y-3 md:block">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Reconnect to restore the remote browser, then pick up where you left off.
            </p>
            <BrowserMissionPicker
              missions={BROWSER_MISSIONS}
              disabled={isConnecting}
              onSelect={onStartMission}
            />
          </div>
        ) : null}

        {showMissionPickerAfterLog ? (
          <div
            className="mb-3 hidden space-y-2 border-t border-gray-200 pt-3 dark:border-gray-700 md:block"
            data-testid="browser-idle-mission-picker-after-log"
          >
            <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
              Start a new session — pick a mission or use Start Browser in the live view.
            </p>
            <BrowserMissionPicker
              missions={BROWSER_MISSIONS}
              disabled={isConnecting}
              onSelect={onStartMission}
            />
          </div>
        ) : null}

        {contextPreview ? (
          <div className="mb-3 rounded-lg border border-sky-200 bg-sky-50/80 p-3 text-xs text-sky-950 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-100">
            <div className="mb-1 flex items-center justify-between gap-2">
              <p className="font-semibold">Record context attached</p>
              {onDismissContextPreview ? (
                <button
                  type="button"
                  onClick={onDismissContextPreview}
                  className="text-[11px] font-medium text-sky-800 underline hover:no-underline dark:text-sky-200"
                >
                  Remove
                </button>
              ) : null}
            </div>
            <pre className="max-h-24 overflow-y-auto whitespace-pre-wrap font-sans text-[11px] leading-relaxed opacity-90">
              {contextPreview}
            </pre>
          </div>
        ) : null}

        {isStreaming ? (
          <div
            className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-primary-200 bg-primary-50/90 px-3 py-2 text-xs text-primary-900 dark:border-primary-800 dark:bg-primary-950/40 dark:text-primary-100"
            data-testid="browser-agent-working"
            role="status"
          >
            <div className="flex min-w-0 items-center gap-2">
              <span
                className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-primary-500 dark:bg-primary-400"
                aria-hidden
              />
              <span>
                Agent is working in the remote browser
                {actionCount && actionCount > 0 ? ` — action ${actionCount}` : ''}
                … Press Esc to cancel.
              </span>
            </div>
          </div>
        ) : null}

        {messages.length > 0 ? (
          <>
            {hiddenLogCount > 0 ? (
              <button
                type="button"
                onClick={() => setLogExpanded(true)}
                className="mb-3 w-full rounded-md border border-dashed border-gray-300 px-3 py-2 text-left text-xs text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800/60"
                data-testid="browser-expand-action-log"
              >
                {hiddenLogCount} earlier action{hiddenLogCount === 1 ? '' : 's'} hidden while the agent
                runs — show full log
              </button>
            ) : null}
            <BrowserActionLog
              messages={visibleMessages}
              contextPreview={contextPreview}
              canRetryLastCommand={canRetryLastCommand}
              onRetryLastCommand={onRetryLastCommand}
              retryLastCommandLabel={retryLastCommandLabel}
            />
          </>
        ) : session ? (
          <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="browser-session-ready-hint">
            Session is live
            {session.currentUrl ? (
              <span className="text-gray-600 dark:text-gray-300"> — opening {session.currentUrl}</span>
            ) : null}
            . Use a suggested prompt or describe the task — the agent will show each action here as it
            works.
            {sessionTimeoutMinutes ? (
              <span className="mt-1 block text-xs text-gray-400 dark:text-gray-500">
                Stays active while you work; idle timeout after {sessionTimeoutMinutes} minutes.
              </span>
            ) : null}
          </p>
        ) : null}

        {followUpSuggestions.length > 0 ? (
          <BrowserFollowUpChips
            suggestions={followUpSuggestions}
            disabled={isDisabled}
            onSelect={(text) => onMessage(text)}
          />
        ) : null}
      </div>
      )}

      <div className="shrink-0 px-3 pb-3 pt-1">
        {previewPrompt && isIdleNoSession ? (
          <button
            type="button"
            disabled={isDisabled}
            onClick={() => onMessage(previewPrompt)}
            className="mb-2 flex w-full items-start gap-2 rounded-lg px-1 py-1 text-left text-sm text-gray-500 transition hover:text-gray-700 disabled:opacity-50 dark:text-gray-400 dark:hover:text-gray-200"
            data-testid="browser-preview-prompt"
          >
            <span className="line-clamp-2">{previewPrompt}</span>
          </button>
        ) : null}

        <BrowserAssistantComposer
          value={input}
          onChange={setInput}
          onSend={handleSend}
          disabled={isDisabled}
          isStreaming={isStreaming}
          placeholder={inputPlaceholder}
          onCancelStream={onCancelStream}
          inputRef={inputRef}
        />

        {session && promptChips.length > 0 && !isStreaming && followUpSuggestions.length === 0 ? (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {promptChips.map((prompt) => (
              <button
                key={prompt}
                type="button"
                disabled={isDisabled}
                onClick={() => onMessage(prompt)}
                className="rounded-full border border-gray-200 bg-white px-3 py-1 text-xs text-gray-700 shadow-sm hover:border-sky-300 hover:bg-sky-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:border-sky-700"
              >
                {prompt}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
